/**
 * Reality Check Variant Selection JavaScript
 * Handles sub-button selection and main option toggling
 */
class RealityCheckVariantSelection {
  constructor() {
    this.container = document.querySelector('.rc-variant-selection');
    
    if (!this.container) {
      console.error('❌ RC Variant Selection: Container not found!');
      return;
    }
    
    // Initialize element cache and observers
    this.elements = {};
    this.observers = new Map();
    this.eventListeners = new Map();
    
    // Get product data from Shopify
    this.getProductData();
    
    // Initialize variant mapping and tracking
    this.initVariantMapping();
    this.currentQuantity = null;
    this.currentSellingPlan = null;
    this.previousQuantity = null;
    this.previousSellingPlan = null;
    
    this.init();
  }

  /**
   * Get product data from Shopify global object
   */
  getProductData() {
    // Try to get product data from various Shopify global sources
    this.productData = window.product || 
                      window.theme?.productData || 
                      JSON.parse(document.querySelector('[data-product-json]')?.textContent || '{}') ||
                      null;
    
    if (!this.productData) {
      console.warn('⚠️ Product data not found - variant objects will only contain ID');
    }
  }

  init() {
    try {
      this.cacheElements();
      this.bindSubButtonEvents();
      this.bindRadioEvents();
      this.initSubtitleSettings();
      this.updateSubtitleText();
      this.initDiscountPricing();
      this.initializeDefaultVariant();
      this.initGalleryObserver();
    } catch (error) {
      console.error('❌ RC Variant Selection: Initialization failed:', error);
    }
  }

  /**
   * Cache frequently used DOM elements for performance
   */
  cacheElements() {
    this.elements = {
      oneTimeOption: this.container.querySelector('.rc-option--one-time'),
      subscriptionOption: this.container.querySelector('.rc-option--subscription'),
      subButtons: this.container.querySelectorAll('.rc-quantity-option'),
      radioButtons: this.container.querySelectorAll('.rc-option__radio'),
      subtitleElements: this.container.querySelectorAll('.rc-option__subtitle'),
      originalPriceElement: this.container.querySelector('.rc-option--one-time [data-original-price]'),
      currentPriceElement: this.container.querySelector('.rc-option--one-time [data-current-price]'),
      atcButton: document.getElementById('main-product-atc-button')
    };


    // Validate critical elements
    if (!this.elements.oneTimeOption) {
      throw new Error('One-time option element not found');
    }
  }

  /**
   * Initialize variant mapping with single variant and quantity/selling plan system
   */
  initVariantMapping() {
    // Single variant for all options - only quantity and selling plans differ
    this.singleVariantId = 55118491189596;
    
    // Mapping for quantities and selling plans
    this.selectionMapping = {
      'one-time-1': { quantity: 1, sellingPlan: null },
      'one-time-2': { quantity: 2, sellingPlan: null },
      'one-time-3': { quantity: 3, sellingPlan: null },
      'subscription-monthly': { quantity: 1, sellingPlan: 692022346076 },
      'subscription-quarterly': { quantity: 1, sellingPlan: 692022378844 },
      'subscription-semiannual': { quantity: 1, sellingPlan: 692022411612 }
    };
    
  }

  /**
   * Bind click events to quantity/period sub-buttons
   */
  bindSubButtonEvents() {
    this.elements.subButtons.forEach((button) => {
      const handler = (e) => {
        e.preventDefault();
        this.handleSubButtonClick(button);
      };
      
      button.addEventListener('click', handler);
      this.eventListeners.set(button, { event: 'click', handler });
    });
  }

  /**
   * Bind change events to main radio buttons
   */
  bindRadioEvents() {
    this.elements.radioButtons.forEach((radio) => {
      const handler = () => {
        if (radio.checked) {
          this.handleRadioChange(radio);
        }
      };
      
      radio.addEventListener('change', handler);
      this.eventListeners.set(radio, { event: 'change', handler });
    });
  }

  /**
   * Handle sub-button click
   * @param {HTMLElement} clickedButton 
   */
  handleSubButtonClick(clickedButton) {
    // Set flag to prevent radio change handler from also updating variant
    this.isSubButtonClick = true;
    
    // Remove active class from ALL sub-buttons (both sections)
    this.elements.subButtons.forEach(btn => {
      btn.classList.remove('rc-quantity-option--active');
    });

    // Add active class to clicked button
    clickedButton.classList.add('rc-quantity-option--active');

    // Find which main option this sub-button belongs to
    const parentOption = clickedButton.closest('.rc-option');
    const parentRadio = parentOption.querySelector('.rc-option__radio');

    // Check the parent radio button if it's not already checked
    if (!parentRadio.checked) {
      parentRadio.checked = true;
    }

    // Update pricing if this is one-time option
    if (parentOption.classList.contains('rc-option--one-time')) {
      const quantity = parseInt(clickedButton.dataset.quantity) || 1;
      this.updateOneTimePricing(quantity);
    }

    this.logSelection();
    this.updateVariant();
    
    // Reset flag after processing
    this.isSubButtonClick = false;
  }

  /**
   * Handle main radio button change
   * @param {HTMLElement} radio 
   */
  handleRadioChange(radio) {
    // Skip if this was triggered by a sub-button click
    if (this.isSubButtonClick) {
      return;
    }
    
    // When main option changes, ensure first sub-button is selected
    const parentOption = radio.closest('.rc-option');
    const subButtons = parentOption.querySelectorAll('.rc-quantity-option');
    
    if (subButtons.length > 0) {
      // Remove active from all sub-buttons
      this.elements.subButtons.forEach(btn => {
        btn.classList.remove('rc-quantity-option--active');
      });

      // Activate first sub-button in the selected option
      subButtons[0].classList.add('rc-quantity-option--active');
    }

    // Always update one-time pricing when radio changes
    if (parentOption.classList.contains('rc-option--one-time')) {
      // Switching to one-time: update pricing based on active quantity
      const quantity = parseInt(subButtons[0].dataset.quantity) || 1;
      this.updateOneTimePricing(quantity);
    } else {
      // Switching away from one-time: reset one-time pricing to 1 kit (no strike price)
      this.updateOneTimePricing(1);
    }

    this.logSelection();
    this.updateVariant();
  }

  /**
   * Get current selection data
   * @returns {Object|null}
   */
  getCurrentSelection() {
    try {
      const checkedRadio = this.container.querySelector('.rc-option__radio:checked');
      const activeSubButton = this.container.querySelector('.rc-quantity-option--active');
      
      if (!checkedRadio || !activeSubButton) {
        console.warn('⚠️ No valid selection found - radio or sub-button missing');
        return null;
      }

      const deliveryType = checkedRadio.value;
      const isOneTime = deliveryType === 'one-time';
      
      return {
        deliveryType: deliveryType,
        isOneTime: isOneTime,
        isSubscription: !isOneTime,
        quantity: isOneTime ? parseInt(activeSubButton.dataset.quantity) || 1 : 1,
        period: isOneTime ? null : activeSubButton.dataset.period,
        button: activeSubButton,
        mainOption: checkedRadio.closest('.rc-option')
      };
    } catch (error) {
      console.error('❌ Error getting current selection:', error);
      return null;
    }
  }

  /**
   * Log current selection for debugging
   */
  logSelection() {
    // This method can be used for debugging if needed
  }

  /**
   * Calculate delivery date range (2-3 business days)
   * @returns {Object} Object with start and end dates
   */
  calculateDeliveryDate() {
    const today = new Date();
    const startDate = this.addBusinessDays(today, 2);
    const endDate = this.addBusinessDays(today, 3);
    
    return {
      start: startDate,
      end: endDate,
      formatted: this.formatDeliveryDateRange(startDate, endDate)
    };
  }

  /**
   * Add business days to a date (excluding weekends)
   * @param {Date} date 
   * @param {number} businessDays 
   * @returns {Date}
   */
  addBusinessDays(date, businessDays) {
    const result = new Date(date);
    let daysAdded = 0;
    
    while (daysAdded < businessDays) {
      result.setDate(result.getDate() + 1);
      
      // Check if it's not a weekend (0 = Sunday, 6 = Saturday)
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        daysAdded++;
      }
    }
    
    return result;
  }

  /**
   * Format delivery date range for display
   * @param {Date} startDate 
   * @param {Date} endDate 
   * @returns {string}
   */
  formatDeliveryDateRange(startDate, endDate) {
    const options = { day: 'numeric', month: 'long' };
    const startFormatted = startDate.toLocaleDateString('de-DE', options);
    const endFormatted = endDate.toLocaleDateString('de-DE', options);
    
    // If same month, show "12. - 14. Juli"
    if (startDate.getMonth() === endDate.getMonth()) {
      const startDay = startDate.getDate();
      return `${startDay}. - ${endFormatted}`;
    } else {
      // Different months, show "30. Juni - 2. Juli"
      return `${startFormatted} - ${endFormatted}`;
    }
  }

  /**
   * Initialize gallery observer to replace polling
   */
  initGalleryObserver() {
    const galleryContainer = document.querySelector('.product-gallery') || 
                           document.querySelector('[data-product-gallery]') ||
                           document.querySelector('.product__media-wrapper');
    
    if (!galleryContainer) {
      console.warn('⚠️ Gallery container not found - gallery change detection disabled');
      return;
    }

    // Observe changes in gallery container
    const galleryObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach(mutation => {
        // Check for class changes (like .is-active, .is-initial)
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'class' || mutation.attributeName === 'aria-current')) {
          shouldUpdate = true;
        }
        // Check for added/removed gallery media elements
        else if (mutation.type === 'childList') {
          shouldUpdate = true;
        }
      });

      if (shouldUpdate) {
        // Debounce multiple rapid changes
        clearTimeout(this.galleryUpdateTimeout);
        this.galleryUpdateTimeout = setTimeout(() => {
          console.log('🔄 Gallery changed - updating selection');
        }, 100);
      }
    });

    galleryObserver.observe(galleryContainer, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'aria-current']
    });

    this.observers.set('gallery', galleryObserver);
  }

initSubtitleSettings() {
  if (!this.elements.oneTimeOption) {
    console.error('❌ One-time option not found for subtitle settings');
    return;
  }

  this.subtitleText = this.elements.oneTimeOption.dataset.subtitleText || 'Diskret geliefert';
  // NEU: das im Liquid gerenderte HTML (inkl. Link) festhalten
  const firstSubtitle = this.elements.subtitleElements && this.elements.subtitleElements[0];
  this.subtitleHTML = firstSubtitle ? firstSubtitle.innerHTML : this.subtitleText;
  this.showDeliveryDate = this.elements.oneTimeOption.dataset.showDeliveryDate === 'true';
}

escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

  /**
   * Update the subtitle text for both options
   */
updateSubtitleText() {
  if (!this.elements.subtitleElements.length) {
    console.warn('⚠️ No subtitle elements found');
    return;
  }

  this.elements.subtitleElements.forEach(element => {
    let html = this.subtitleHTML;

    // Lieferdatum nur bei one-time und wenn aktiviert
    if (element.closest('.rc-option--one-time') && this.showDeliveryDate) {
      const deliveryDate = this.calculateDeliveryDate();
      html += ` ${this.escapeHtml(deliveryDate.formatted)}`;
    }

    element.innerHTML = html;
  });
}

  /**
   * Initialize discount pricing for one-time option
   */
  initDiscountPricing() {
    if (!this.elements.oneTimeOption) {
      console.error('❌ One-time option not found for pricing initialization');
      return;
    }

    // Get pricing data from data attributes
    this.basePrice = parseFloat(this.elements.oneTimeOption.dataset.basePrice) || 80;
    // Support comma decimal separator: replace comma with dot, then parse
    this.discount2Kits = parseFloat((this.elements.oneTimeOption.dataset['discount-2'] || '0').replace(',', '.')) || 0;
    this.discount3Kits = parseFloat((this.elements.oneTimeOption.dataset['discount-3'] || '0').replace(',', '.')) || 0;
    this.discountType = this.elements.oneTimeOption.dataset.discountType || 'percentage';
    this.pricingMode = this.elements.oneTimeOption.dataset.pricingMode || 'total';
    this.unitLabelPer = this.elements.oneTimeOption.dataset.unitLabelPer || 'pro Kit';

    // Update prices on quantity selection
    this.updateOneTimePricing(1); // Initialize with 1 kit
  }

  /**
   * Update pricing for one-time option based on quantity
   * @param {number} quantity 
   */
  updateOneTimePricing(quantity) {
    if (!this.elements.originalPriceElement || !this.elements.currentPriceElement) {
      console.warn('⚠️ Price elements not found');
      return;
    }

    const discountValue = quantity === 2 ? this.discount2Kits : 
                          quantity === 3 ? this.discount3Kits : 0;
    const isBasePricing = this.pricingMode === 'base';
    const isPercentageDiscount = this.discountType === 'percentage';
    
    // Add/remove multi-kit class for small screen font size adjustment
    if (quantity >= 2) {
      this.elements.oneTimeOption.classList.add('rc-option--multi-kit');
    } else {
      this.elements.oneTimeOption.classList.remove('rc-option--multi-kit');
    }

    if (this.pricingMode === 'total') {
      // Mode 1: Total Price (e.g., 160€ for 2 kits)
      const originalTotal = this.basePrice * quantity;
      let currentTotal = originalTotal;
      
      if (discountValue > 0) {
        if (isPercentageDiscount) {
          // Percentage discount: reduce by percentage
          currentTotal = originalTotal - (originalTotal * discountValue / 100);
        } else {
          // Fixed discount: reduce by fixed amount per kit
          currentTotal = originalTotal - (discountValue * quantity);
        }
      }

      // Format prices without "pro Kit" for total pricing
      this.elements.originalPriceElement.textContent = this.formatPrice(originalTotal, quantity, false);
      this.elements.currentPriceElement.textContent = this.formatPrice(currentTotal, quantity, false);
    } else {
      // Mode 2: Base Price per Kit (e.g., 80€ per kit with discount)
      const originalPrice = this.basePrice;
      let currentPrice = originalPrice;
      
      if (discountValue > 0) {
        if (isPercentageDiscount) {
          // Percentage discount: reduce by percentage
          currentPrice = originalPrice - (originalPrice * discountValue / 100);
        } else {
          // Fixed discount: reduce by fixed amount per kit
          currentPrice = originalPrice - discountValue;
        }
      }

      // Format prices with "pro Kit" only for current price, not for strike price
      this.elements.originalPriceElement.textContent = this.formatPrice(originalPrice, quantity, false);
      this.elements.currentPriceElement.textContent = this.formatPrice(currentPrice, quantity, isBasePricing);
    }

    // Show/hide strike price
    this.elements.originalPriceElement.style.display = discountValue > 0 ? 'inline' : 'none';
  }

  /**
   * Format price without rounding - show full precision
   * @param {number} price 
   * @param {number} quantity - current selected quantity
   * @param {boolean} isBasePricing - whether we're in base pricing mode
   * @returns {string}
   */
  formatPrice(price, quantity = 1, isBasePricing = false) {
    // Remove trailing zeros after decimal point, but keep meaningful decimals
    const formattedPrice = parseFloat(price.toFixed(2)) + '€';
    
    // Add per-unit suffix for base pricing mode when quantity >= 2
    if (isBasePricing && quantity >= 2) {
      return formattedPrice + ' ' + (this.unitLabelPer || 'pro Kit');
    }
    
    return formattedPrice;
  }


  /**
   * Initialize default variant (first selection)
   */
  initializeDefaultVariant() {
    this.updateVariant();
  }

  /**
   * Get variant key based on current selection
   * @returns {string|null}
   */
  getVariantKey() {
    const selection = this.getCurrentSelection();
    if (!selection) return null;

    if (selection.isOneTime) {
      return `one-time-${selection.quantity}`;
    } else {
      return `subscription-${selection.period}`;
    }
  }

  /**
   * Update current selection and trigger variant:change event
   */
  updateVariant() {
    try {
      const selectionKey = this.getVariantKey();
      if (!selectionKey) {
        console.warn('⚠️ Could not determine variant key from current selection');
        return;
      }

      if (!this.selectionMapping[selectionKey]) {
        console.error('❌ Unknown selection key:', selectionKey, 'Available keys:', Object.keys(this.selectionMapping));
        return;
      }

      const mappingData = this.selectionMapping[selectionKey];
      const newQuantity = mappingData.quantity;
      const newSellingPlan = mappingData.sellingPlan;
      
      // Check if both quantity and selling plan are the same to avoid duplicate events
      if (this.currentQuantity === newQuantity && this.currentSellingPlan === newSellingPlan) {
        return;
      }

      // Store previous values
      this.previousQuantity = this.currentQuantity;
      this.previousSellingPlan = this.currentSellingPlan;
      
      // Set new current values
      this.currentQuantity = newQuantity;
      this.currentSellingPlan = newSellingPlan;

      // Trigger variant:change event
      this.triggerVariantChangeEvent();
    } catch (error) {
      console.error('❌ Error updating variant:', error);
    }
  }

  /**
   * Get the single variant object
   * @returns {Object}
   */
  getVariantObject() {
    if (!this.productData || !this.productData.variants) {
      return { id: this.singleVariantId }; // Fallback to minimal object
    }
    
    return this.productData.variants.find(variant => variant.id === this.singleVariantId) || { id: this.singleVariantId };
  }

  /**
   * Update the product form's variant, quantity and selling plan inputs directly
   */
  updateProductForm() {
    // Find the product form
    const productForm = document.querySelector('form[action*="/cart/add"]') || 
                       document.querySelector('product-form form');
    
    if (productForm) {
      // Update variant input (always the same single variant)
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = this.singleVariantId;
      }

      // Update or create quantity input
      let quantityInput = productForm.querySelector('input[name="quantity"]');
      if (!quantityInput) {
        // Create quantity input if it doesn't exist
        quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        productForm.appendChild(quantityInput);
      }
      quantityInput.value = this.currentQuantity || 1;

      // Update or create selling plan input
      let sellingPlanInput = productForm.querySelector('input[name="selling_plan"]');
      if (!sellingPlanInput) {
        // Create selling plan input if it doesn't exist
        sellingPlanInput = document.createElement('input');
        sellingPlanInput.type = 'hidden';
        sellingPlanInput.name = 'selling_plan';
        productForm.appendChild(sellingPlanInput);
      }
      
      // Set selling plan value (empty string for one-time purchases)
      sellingPlanInput.value = this.currentSellingPlan || '';
    }
  }

  /**
   * Trigger the variant:change event as per Shopify theme docs
   */
  triggerVariantChangeEvent() {
    // First, update the form directly
    this.updateProductForm();

    // Find the product form (typical Shopify selector)
    const productForm = document.querySelector('form[action*="/cart/add"]') || 
                       document.querySelector('product-form form') ||
                       document.querySelector('[data-product-form]') ||
                       document.body; // Fallback to body

    // Get the single variant object (same for all selections)
    const currentVariant = this.getVariantObject();
    const previousVariant = this.previousQuantity || this.previousSellingPlan ? this.getVariantObject() : null;

    const event = new CustomEvent('variant:change', {
      bubbles: true,
      detail: {
        variant: currentVariant,
        previousVariant: previousVariant
      }
    });

    productForm.dispatchEvent(event);
  }

  /**
   * Clean up all event listeners and observers
   * Call this when the component is being destroyed
   */
  destroy() {
    try {
      // Clean up all event listeners
      this.eventListeners.forEach((listenerData, key) => {
        if (listenerData.target) {
          listenerData.target.removeEventListener(listenerData.event, listenerData.handler);
        } else if (typeof key === 'object' && key.removeEventListener) {
          // For element-based listeners where the element is the key
          key.removeEventListener(listenerData.event, listenerData.handler);
        }
      });
      this.eventListeners.clear();

      // Clean up all observers
      this.observers.forEach(observer => {
        if (observer && typeof observer.disconnect === 'function') {
          observer.disconnect();
        }
      });
      this.observers.clear();

      // Clear timeouts
      if (this.galleryUpdateTimeout) {
        clearTimeout(this.galleryUpdateTimeout);
        this.galleryUpdateTimeout = null;
      }

      // Clear element references
      this.elements = {};
      
      console.log('✅ RC Variant Selection: Cleaned up successfully');
    } catch (error) {
      console.error('❌ RC Variant Selection: Cleanup failed:', error);
    }
  }
}

// Initialize only once when DOM is ready
function initializeRealityCheckVariantSelection() {
  // Prevent double initialization
  if (window.rcVariantSelectionInitialized) {
    return;
  }
  window.rcVariantSelectionInitialized = true;
  
  // Store instance globally for potential cleanup
  window.rcVariantSelectionInstance = new RealityCheckVariantSelection();
}

// Initialize when DOM is loaded or if already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeRealityCheckVariantSelection);
} else {
  initializeRealityCheckVariantSelection();
} 